import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { LoadingBlock, ErrorState } from "@/components/ds";
import { PageTitle, Stat, Badge, Card, money, pct } from "./_ui";

const METRICS = [
  { key: "total_paid_sales", label: "Paid Sales" },
  { key: "initial_revenue_collected", label: "Revenue", money: true },
  { key: "growth", label: "Growth" },
  { key: "pro", label: "Pro" },
  { key: "custom", label: "Custom" },
];

export default function SalesDashboard() {
  const [rank, setRank] = useState("total_paid_sales");
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["sales-dashboard"], queryFn: () => api.salesDashboard() });
  if (isLoading) return <LoadingBlock rows={6} />;
  if (isError || !data) return <ErrorState onRetry={refetch} />;
  const t = data.totals, tiers = data.tiers;
  const reps = [...(data.reps || [])].sort((a, b) => (b[rank] ?? 0) - (a[rank] ?? 0)).slice(0, 8);

  return (
    <div data-testid="sales-dashboard">
      <PageTitle title="Sales — Company Overview" subtitle="Current performance period (rolling 30-day windows per rep)." />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Total Paid Sales" value={t.total_sales} testid="sd-total-sales" />
        <Stat label="Total Initial Revenue" value={money(t.initial_revenue)} tone="emerald" testid="sd-revenue" />
        <Stat label="Total Compensation Owed" value={money(t.comp_owed)} tone="indigo" testid="sd-comp-owed" />
        <Stat label="Total Paid" value={money(t.paid)} tone="blue" testid="sd-paid" />
        <Stat label="Growth Sales" value={t.growth} />
        <Stat label="Pro Sales" value={t.pro} />
        <Stat label="Custom Sales" value={t.custom} />
        <Stat label="Starter Sales" value={t.starter} />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Base Commission Owed" value={money(t.base_commission)} />
        <Stat label="Bonuses Owed" value={money(t.bonuses)} tone="amber" />
        <Stat label="Reps at Quota" value={tiers.at_quota} tone="emerald" />
        <Stat label="Reps Below Quota" value={tiers.below_quota} />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-sm shadow-sm" data-testid="sd-tiers">
        <span className="font-medium text-slate-700">Reps reaching:</span>
        <span>15+: <b>{tiers.r15}</b></span><span>20+: <b>{tiers.r20}</b></span>
        <span>25+: <b>{tiers.r25}</b></span><span>30+: <b>{tiers.r30}</b></span>
        <span className="ml-auto flex items-center gap-2">Growth Mix: <b>{pct(data.growth_mix_percent)}</b> <Badge status={data.growth_mix_status} /></span>
      </div>

      <div className="mt-6">
        <Card title="Top Representatives" right={
          <select value={rank} onChange={(e) => setRank(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1 text-xs" data-testid="sd-rank-select">
            {METRICS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
        }>
          {reps.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">No sales in the current period yet.</p> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-400">
                <th className="py-2">Rep</th><th>Sales</th><th>Quota</th><th>Growth Mix</th><th>Base</th><th>Bonuses</th><th>Total Owed</th></tr></thead>
              <tbody>
                {reps.map((r: any) => (
                  <tr key={r.user_id} className="border-t border-slate-50" data-testid={`sd-rep-${r.user_id}`}>
                    <td className="py-2"><Link to={`/internal/sales/reps/${r.user_id}`} className="font-medium text-blue-600 hover:underline">{r.rep_name}</Link></td>
                    <td>{r.total_paid_sales} <Badge status={r.quota_status} /></td>
                    <td>{r.total_paid_sales}/{r.quota}</td>
                    <td>{pct(r.growth_mix_percent)}</td>
                    <td>{money(r.base_commission)}</td>
                    <td>{money(r.total_bonuses)}</td>
                    <td className="font-semibold">{money(r.total_owed)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  );
}
